/**
 * AtlasWorldsModule — the "My Worlds" panel (M): create, name, share, and hop
 * between as many worlds as you like (beyond the one seeded personal world).
 *
 * Everything here drives EXISTING server machinery — the client only mirrors:
 *  - Create → `spawnWorld` (charges the SOV hosting fee; the creator is the
 *    OWNER and therefore implicitly holds every capability — "admin of their
 *    own world", exactly like the personal world), then the chosen visibility
 *    is applied via `setWorldVisibility` once the row replicates.
 *  - Visibility (public | unlisted | private) → `setWorldVisibility`.
 *  - Access → `setWorldRole` grants by USERNAME (resolved to an identity from
 *    the replicated `account` table): build / decorate / script, revoke back
 *    to visitor. The server re-validates owner/admin on every grant.
 *  - Enter → the same `atlas:enter-world` flow Orbits use.
 *
 * "Shared with me" lists worlds someone granted you a role in. Public worlds
 * of other players are discovered via the Discovery feed, not here.
 */

import type { App, IModule } from 'enginex';
import { WORLD_PRESETS } from '../lib/personal_presets.js';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';

const VISIBILITIES = ['private', 'unlisted', 'public'] as const;
const GRANT_ROLES = ['build', 'decorate', 'script', 'visitor'] as const;
const SIZES = [64, 128, 256, 512] as const;

export class AtlasWorldsModule implements IModule {
  readonly name = 'AtlasWorlds';

  private win: UiWindow | null = null;
  private pingTimer: number | null = null;
  private listEl: HTMLDivElement | null = null;
  private sharedEl: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private nameInput: HTMLInputElement | null = null;
  private sizeSel: HTMLSelectElement | null = null;
  private visSel: HTMLSelectElement | null = null;
  /** Create staging: apply this visibility when MY world row with this name arrives. */
  private pendingVis: { name: string; visibility: string } | null = null;
  private openAccess = new Set<string>(); // worldId keys with the access editor expanded

  init(_app: App): void {
    void _app;
    window.addEventListener('keydown', (e) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // F8 (was M): the SWG-style local map claimed M (2026-08-06); the menu
      // bar's Worlds button dispatches F8 now.
      if (e.code === 'F8' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        this.toggle();
      }
    });
    bus.on('atlas:account-ready', () => {
      const conn = atlas.conn;
      if (!conn) return;
      // Row events (world/world_role sync bursts) coalesce behind a trailing
      // debounce and re-render only while the panel is OPEN — toggle() renders
      // fresh on every open, so a closed panel never pays the DOM rebuild or
      // the account scans (doctrine law 4: debounce row-storm fan-outs).
      const ping = (): void => {
        if (this.pingTimer !== null) return;
        this.pingTimer = window.setTimeout(() => {
          this.pingTimer = null;
          if (this.win?.isOpen()) this.render();
        }, 120);
      };
      conn.db.world.onInsert((_c, row) => {
        // Complete a staged create: my new row arrived → apply the chosen visibility.
        if (
          this.pendingVis &&
          row.name === this.pendingVis.name &&
          row.owner.toHexString() === atlas.identityHex
        ) {
          const vis = this.pendingVis.visibility;
          this.pendingVis = null;
          if (row.visibility !== vis) {
            void conn.reducers
              .setWorldVisibility({ worldId: row.worldId, visibility: vis })
              .catch((err: Error) => this.setNote(`visibility: ${err.message}`, 'err'));
          }
          this.setNote(`"${row.name}" created — you are its owner/admin.`, 'ok');
        }
        ping();
      });
      conn.db.world.onUpdate(ping);
      conn.db.world.onDelete(ping);
      conn.db.worldRole.onInsert(ping);
      conn.db.worldRole.onUpdate(ping);
      conn.db.worldRole.onDelete(ping);
    });
    bus.on('atlas:world-entered', () => this.render());
  }

  private toggle(): void {
    if (this.win?.isOpen()) {
      this.win.setOpen(false);
      return;
    }
    this.build();
    this.win!.setOpen(true);
    this.render();
  }

  // ─── Panel skeleton ───────────────────────────────────────────────────────────

  private build(): void {
    if (this.win) return;
    // SWG-style window: draggable, resizable, layout persisted per id.
    this.win = makeWindow({
      id: 'worlds',
      title: 'My Worlds',
      icon: 'globe',
      width: 460,
      minWidth: 380,
      minHeight: 260,
      x: 12,
      y: 48,
    });
    const p = document.createElement('div');
    p.style.cssText =
      'display:flex;flex-direction:column;padding:10px;gap:10px;' +
      'color:#dde;font:12px/1.5 system-ui;box-sizing:border-box';
    this.win.body.appendChild(p);

    // ── Create form ──
    const form = document.createElement('div');
    form.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;' +
      'background:rgba(255,255,255,.04);border:1px solid #2a3f55;border-radius:8px;padding:8px';
    this.nameInput = document.createElement('input');
    this.nameInput.placeholder = 'New world name…';
    this.nameInput.maxLength = 64;
    this.nameInput.style.cssText = 'flex:1;min-width:140px;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:6px 8px';
    form.appendChild(this.nameInput);
    this.sizeSel = document.createElement('select');
    for (const s of SIZES) {
      const o = document.createElement('option');
      o.value = String(s);
      o.textContent = `${s} m`;
      if (s === 128) o.selected = true;
      this.sizeSel.appendChild(o);
    }
    this.sizeSel.style.cssText = 'background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:6px';
    form.appendChild(this.sizeSel);
    this.visSel = document.createElement('select');
    for (const v of VISIBILITIES) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = v;
      this.visSel.appendChild(o);
    }
    this.visSel.style.cssText = this.sizeSel.style.cssText;
    form.appendChild(this.visSel);
    const createBtn = document.createElement('button');
    createBtn.textContent = 'Create';
    createBtn.style.cssText = 'padding:6px 14px;background:#144a2f;color:#d8ffe8;border:1px solid #2c7a4f;border-radius:6px;font-weight:600;cursor:pointer';
    createBtn.onclick = () => this.create();
    form.appendChild(createBtn);
    const fee = document.createElement('div');
    fee.style.cssText = 'width:100%;opacity:.6';
    fee.textContent = 'Hosting charges a one-time SOV fee (scales with size). You own the world — full build/admin rights, like your personal world.';
    form.appendChild(fee);
    p.appendChild(form);

    this.note = document.createElement('div');
    this.note.style.cssText = 'min-height:14px;opacity:.95';
    p.appendChild(this.note);

    const ownedTitle = document.createElement('div');
    ownedTitle.style.cssText = 'font-weight:600;border-bottom:1px solid #2a3f55;padding-bottom:3px';
    ownedTitle.textContent = 'Worlds I own';
    p.appendChild(ownedTitle);
    this.listEl = document.createElement('div');
    this.listEl.style.cssText = 'display:flex;flex-direction:column;gap:6px';
    p.appendChild(this.listEl);

    const sharedTitle = document.createElement('div');
    sharedTitle.style.cssText = 'font-weight:600;border-bottom:1px solid #2a3f55;padding-bottom:3px;margin-top:4px';
    sharedTitle.textContent = 'Shared with me';
    p.appendChild(sharedTitle);
    this.sharedEl = document.createElement('div');
    this.sharedEl.style.cssText = 'display:flex;flex-direction:column;gap:4px';
    p.appendChild(this.sharedEl);

    const foot = document.createElement('div');
    foot.style.cssText = 'opacity:.6';
    foot.textContent = 'Public worlds by other players surface in the Discovery feed.';
    p.appendChild(foot);

  }

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    if (!this.note) return;
    this.note.textContent = text;
    this.note.style.color = cls === 'err' ? '#ff9c9c' : cls === 'ok' ? '#9cffc4' : '#dde';
  }

  // ─── Actions ──────────────────────────────────────────────────────────────────

  private create(): void {
    const conn = atlas.conn;
    if (!conn || !this.nameInput || !this.sizeSel || !this.visSel) return;
    const name = this.nameInput.value.trim();
    if (!name) {
      this.setNote('Give the world a name first.', 'err');
      return;
    }
    this.pendingVis = { name, visibility: this.visSel.value };
    this.setNote(`Creating "${name}"…`);
    void conn.reducers
      .spawnWorld({
        name,
        sizeM: Number(this.sizeSel.value),
        kind: 'individual',
        lifecycle: 'persistent',
        governanceTemplate: 'solo',
        capacity: 32,
      })
      .then(() => { if (this.nameInput) this.nameInput.value = ''; })
      .catch((err: Error) => {
        this.pendingVis = null;
        this.setNote(err.message, 'err'); // e.g. insufficient SOV for the hosting fee
      });
  }

  private grant(worldId: bigint, username: string, role: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    const uname = username.trim().toLowerCase();
    if (!uname) {
      this.setNote('Type the player username to grant.', 'err');
      return;
    }
    let target: Identity | null = null;
    for (const a of conn.db.account.iter()) {
      if (a.username.toLowerCase() === uname) { target = a.identity; break; }
    }
    if (!target) {
      this.setNote(`No player named "${username}" found.`, 'err');
      return;
    }
    if (target.toHexString() === atlas.identityHex) {
      this.setNote('You already own this world.', 'err');
      return;
    }
    void conn.reducers
      .setWorldRole({ worldId, target, role, caps: '' })
      .then(() => this.setNote(`Granted ${role} to ${username}.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !this.listEl || !this.sharedEl) return;
    const me = atlas.identityHex;
    this.listEl.replaceChildren();
    this.sharedEl.replaceChildren();
    if (!me) return;

    // One account pass per render — the per-row scan was O(worlds × accounts).
    const nameByHex = new Map<string, string>();
    for (const a of conn.db.account.iter()) nameByHex.set(a.identity.toHexString(), a.username);
    const usernameOf = (hex: string): string => nameByHex.get(hex) ?? `${hex.slice(0, 8)}…`;

    // ── Worlds I own ──
    let owned = 0;
    for (const w of conn.db.world.iter()) {
      if (w.owner.toHexString() !== me || w.kind === 'commons') continue;
      owned++;
      const key = w.worldId.toString();
      const row = document.createElement('div');
      row.style.cssText = 'background:rgba(255,255,255,.04);border:1px solid #2a3f55;border-radius:8px;padding:7px 9px;display:flex;flex-direction:column;gap:5px';

      const top = document.createElement('div');
      top.style.cssText = 'display:flex;gap:8px;align-items:center';
      const nm = document.createElement('b');
      nm.textContent = w.name;
      nm.style.flex = '1';
      top.appendChild(nm);
      const meta = document.createElement('span');
      meta.style.opacity = '.6';
      meta.textContent = `${w.kind} · ${w.sizeM} m`;
      top.appendChild(meta);

      // Draft-first lifecycle: anything not public IS a draft — only you (and
      // your grants) can enter until you publish.
      if (w.visibility !== 'public') {
        const badge = document.createElement('span');
        badge.textContent = 'DRAFT';
        badge.style.cssText = 'background:#3a2e12;color:#e3b341;border:1px solid #5c4a1e;border-radius:4px;padding:1px 6px;font-size:10px;letter-spacing:.5px';
        top.appendChild(badge);
      }

      const vis = document.createElement('select');
      for (const v of VISIBILITIES) {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = v;
        if (w.visibility === v) o.selected = true;
        vis.appendChild(o);
      }
      vis.style.cssText = 'background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:3px';
      vis.onchange = () => {
        // Publishing (→ public) runs the PRE-PUBLISH CHECKLIST first — the
        // level-editor rule: surface problems at publish INTENT, not after
        // visitors arrive. Non-public flips (back to draft) apply directly.
        if (vis.value === 'public' && !this.prePublishChecklist(conn, w)) {
          vis.value = w.visibility; // stay a draft
          return;
        }
        void conn.reducers
          .setWorldVisibility({ worldId: w.worldId, visibility: vis.value })
          .then(() => this.setNote(vis.value === 'public' ? `"${w.name}" is PUBLISHED — others can join.` : `"${w.name}" is now a ${vis.value} draft.`, 'ok'))
          .catch((err: Error) => this.setNote(err.message, 'err'));
      };
      top.appendChild(vis);

      const enter = document.createElement('button');
      const here = atlas.currentWorldId === w.worldId;
      enter.textContent = here ? 'Here' : 'Enter';
      enter.disabled = here;
      enter.style.cssText = 'padding:4px 10px;background:#123a5c;color:#dff2ff;border:1px solid #2e6da4;border-radius:5px;cursor:pointer';
      if (here) enter.style.opacity = '.5';
      enter.onclick = () => bus.emit('atlas:enter-world', { worldId: w.worldId });
      top.appendChild(enter);

      if (here) {
        const snap = document.createElement('button');
        snap.textContent = 'Snapshot';
        snap.title = 'Save a draft snapshot of every part in this world (kept locally, last 3)';
        snap.style.cssText = 'padding:4px 8px;background:#1c2a3c;color:#cdd;border:1px solid #35506a;border-radius:5px;cursor:pointer';
        snap.onclick = () => this.saveSnapshot(conn, w.worldId, w.name);
        top.appendChild(snap);
        const rest = document.createElement('button');
        const stash = this.snapshots(w.worldId);
        rest.textContent = `Restore (${stash.length})`;
        rest.disabled = !stash.length;
        rest.title = stash.length ? `Latest: ${new Date(stash[0].at).toLocaleString()}` : 'No snapshots yet';
        rest.style.cssText = 'padding:4px 8px;background:#1c2a3c;color:#cdd;border:1px solid #35506a;border-radius:5px;cursor:pointer';
        if (!stash.length) rest.style.opacity = '.5';
        rest.onclick = () => void this.restoreSnapshot(conn, w.worldId);
        top.appendChild(rest);
      }

      const accessBtn = document.createElement('button');
      accessBtn.textContent = this.openAccess.has(key) ? 'Access ▾' : 'Access ▸';
      accessBtn.style.cssText = 'padding:4px 8px;background:#1c2a3c;color:#cdd;border:1px solid #35506a;border-radius:5px;cursor:pointer';
      accessBtn.onclick = () => {
        if (this.openAccess.has(key)) this.openAccess.delete(key);
        else this.openAccess.add(key);
        this.render();
      };
      top.appendChild(accessBtn);
      row.appendChild(top);

      // ── Access editor (grants) ──
      if (this.openAccess.has(key)) {
        const acc = document.createElement('div');
        acc.style.cssText = 'display:flex;flex-direction:column;gap:4px;border-top:1px dashed #2a3f55;padding-top:5px';
        for (const r of conn.db.worldRole.iter()) {
          if (r.worldId !== w.worldId) continue;
          const hex = r.identity.toHexString();
          if (hex === me) continue; // own bootstrap row — not a "share"
          const line = document.createElement('div');
          line.style.cssText = 'display:flex;gap:6px;align-items:center';
          const who = document.createElement('span');
          who.textContent = `${usernameOf(hex)} — ${r.role}`;
          who.style.flex = '1';
          line.appendChild(who);
          if (r.role !== 'visitor') {
            const rv = document.createElement('button');
            rv.textContent = 'Revoke';
            rv.style.cssText = 'padding:2px 8px;background:#4a1420;color:#ffd8dd;border:1px solid #7a2c3f;border-radius:5px;cursor:pointer';
            rv.onclick = () => {
              void conn.reducers
                .setWorldRole({ worldId: w.worldId, target: r.identity, role: 'visitor', caps: '' })
                .then(() => this.setNote(`${usernameOf(hex)} is now a visitor.`, 'ok'))
                .catch((err: Error) => this.setNote(err.message, 'err'));
            };
            line.appendChild(rv);
          }
          acc.appendChild(line);
        }
        const grantRow = document.createElement('div');
        grantRow.style.cssText = 'display:flex;gap:6px';
        const uin = document.createElement('input');
        uin.placeholder = 'username…';
        uin.style.cssText = 'flex:1;background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px 7px';
        grantRow.appendChild(uin);
        const rsel = document.createElement('select');
        for (const g of GRANT_ROLES) {
          const o = document.createElement('option');
          o.value = g;
          o.textContent = g;
          rsel.appendChild(o);
        }
        rsel.style.cssText = 'background:#0d1420;color:#dde;border:1px solid #35506a;border-radius:5px;padding:4px';
        grantRow.appendChild(rsel);
        const gbtn = document.createElement('button');
        gbtn.textContent = 'Grant';
        gbtn.style.cssText = 'padding:4px 10px;background:#144a2f;color:#d8ffe8;border:1px solid #2c7a4f;border-radius:5px;cursor:pointer';
        gbtn.onclick = () => this.grant(w.worldId, uin.value, rsel.value);
        grantRow.appendChild(gbtn);
        acc.appendChild(grantRow);
        // ── WORLD STYLE (personal worlds): blank / mansion / penthouse / cottage ──
        if (w.kind === 'personal') {
          const styleRow = document.createElement('div');
          styleRow.style.cssText = 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-top:6px';
          const lbl = document.createElement('span');
          lbl.textContent = 'Style:';
          lbl.style.cssText = 'opacity:.7;font-size:12px';
          styleRow.appendChild(lbl);
          const current = (() => {
            try {
              return (conn.db as unknown as { worldPreset?: { worldId: { find(id: bigint): { preset: string } | null | undefined } } }).worldPreset?.worldId.find(w.worldId)?.preset ?? 'blank';
            } catch { return 'blank'; }
          })();
          for (const ps of WORLD_PRESETS) {
            const b = document.createElement('button');
            b.textContent = ps.name;
            b.title = ps.desc;
            const active = current === ps.key;
            b.style.cssText = `padding:3px 10px;border-radius:5px;cursor:pointer;font-size:12px;`
              + (active ? 'background:#3a5c2e;color:#e2ffd8;border:1px solid #5e9a4a' : 'background:#0d1420;color:#bcd;border:1px solid #35506a');
            b.onclick = () => {
              (conn.reducers as unknown as { setWorldPreset(a2: { worldId: bigint; preset: string }): Promise<void> })
                .setWorldPreset({ worldId: w.worldId, preset: ps.key })
                .then(() => { bus.emit('atlas:status', { text: `🏡 ${ps.name} — ${ps.desc}` }); this.render(); })
                .catch((e: Error) => bus.emit('atlas:status', { text: `⚠ ${String(e.message ?? e)}` }));
            };
            styleRow.appendChild(b);
          }
          acc.appendChild(styleRow);
        }
        row.appendChild(acc);
      }
      this.listEl.appendChild(row);
    }
    if (owned === 0) {
      const empty = document.createElement('div');
      empty.style.opacity = '.6';
      empty.textContent = 'None yet — your seeded personal world appears here once replicated.';
      this.listEl.appendChild(empty);
    }

    // ── Shared with me ──
    let shared = 0;
    for (const r of conn.db.worldRole.iter()) {
      if (r.identity.toHexString() !== me || r.role === 'visitor') continue;
      const w = conn.db.world.worldId.find(r.worldId);
      if (!w || w.owner.toHexString() === me) continue;
      shared++;
      const line = document.createElement('div');
      line.style.cssText = 'display:flex;gap:8px;align-items:center';
      const nm = document.createElement('span');
      nm.textContent = `${w.name} — ${r.role} (by ${usernameOf(w.owner.toHexString())})`;
      nm.style.flex = '1';
      line.appendChild(nm);
      const enter = document.createElement('button');
      const here = atlas.currentWorldId === w.worldId;
      enter.textContent = here ? 'Here' : 'Enter';
      enter.disabled = here;
      enter.style.cssText = 'padding:3px 10px;background:#123a5c;color:#dff2ff;border:1px solid #2e6da4;border-radius:5px;cursor:pointer';
      enter.onclick = () => bus.emit('atlas:enter-world', { worldId: w.worldId });
      line.appendChild(enter);
      this.sharedEl.appendChild(line);
    }
    if (shared === 0) {
      const empty = document.createElement('div');
      empty.style.opacity = '.6';
      empty.textContent = 'No one has granted you a role in their world yet.';
      this.sharedEl.appendChild(empty);
    }
  }

  dispose(): void {
    this.win?.root.remove();
    this.win = null;
  }
  // ─── Draft lifecycle: pre-publish checklist + snapshots ───────────────────────

  /** Row-level checks before a world goes public (parts, budget, bounds).
   *  Returns true to proceed. Advisory — the server owns all real gates. */
  private prePublishChecklist(conn: import('../module_bindings/index.js').DbConnection, w: { worldId: bigint; name: string; sizeM: number }): boolean {
    interface P { worldId: bigint; posX: number; posZ: number }
    let parts = 0;
    let outOfBounds = 0;
    const half = w.sizeM / 2;
    for (const p of conn.db.part.iter() as Iterable<P>) {
      if (p.worldId !== w.worldId) continue;
      parts++;
      if (Math.abs(p.posX) > half || Math.abs(p.posZ) > half) outOfBounds++;
    }
    const b = conn.db.worldBuildBudget.worldId.find(w.worldId) as { liUsed: number; liMax: number } | undefined;
    const lines: string[] = [];
    lines.push(parts === 0 ? '⚠ The world is EMPTY — visitors will find nothing.' : `✓ ${parts} parts placed.`);
    if (b) lines.push(b.liUsed > b.liMax ? `✗ OVER Land-Impact budget (${b.liUsed}/${b.liMax}).` : `✓ Land Impact ${b.liUsed}/${b.liMax}.`);
    if (outOfBounds) lines.push(`⚠ ${outOfBounds} part(s) outside the world bounds.`);
    lines.push('Tip: walk the world from spawn like a new visitor before publishing.');
    return window.confirm(`Publish "${w.name}" for others to join?\n\n${lines.join('\n')}`);
  }

  private snapshots(worldId: bigint): Array<{ at: number; parts: unknown[] }> {
    try {
      return JSON.parse(localStorage.getItem(`atlas:draft:${worldId}`) ?? '[]') as Array<{ at: number; parts: unknown[] }>;
    } catch { return []; }
  }

  /** Save every part row of the CURRENT world as a local draft snapshot
   *  ("treat content like software: rollbacks"). Keeps the last 3. */
  private saveSnapshot(conn: import('../module_bindings/index.js').DbConnection, worldId: bigint, name: string): void {
    interface P {
      worldId: bigint; primType: string; meshRef: string;
      posX: number; posY: number; posZ: number;
      rotX: number; rotY: number; rotZ: number; rotW: number;
      scaleX: number; scaleY: number; scaleZ: number;
      shapeJson: string; materialJson: string; physics: boolean;
    }
    const parts: unknown[] = [];
    for (const p of conn.db.part.iter() as Iterable<P>) {
      if (p.worldId !== worldId) continue;
      parts.push({
        primType: p.primType, meshRef: p.meshRef,
        posX: p.posX, posY: p.posY, posZ: p.posZ,
        rotX: p.rotX, rotY: p.rotY, rotZ: p.rotZ, rotW: p.rotW,
        scaleX: p.scaleX, scaleY: p.scaleY, scaleZ: p.scaleZ,
        shapeJson: p.shapeJson, materialJson: p.materialJson, physics: p.physics,
      });
    }
    const stash = this.snapshots(worldId);
    stash.unshift({ at: Date.now(), parts });
    localStorage.setItem(`atlas:draft:${worldId}`, JSON.stringify(stash.slice(0, 3)));
    this.setNote(`Snapshot saved — ${parts.length} parts ("${name}", ${new Date().toLocaleTimeString()}).`, 'ok');
    this.render();
  }

  /** Restore the latest snapshot: delete current parts, re-rez the saved set.
   *  Uploaded meshes without a catalog key are skipped with a note. */
  private async restoreSnapshot(conn: import('../module_bindings/index.js').DbConnection, worldId: bigint): Promise<void> {
    const stash = this.snapshots(worldId);
    const snap = stash[0];
    if (!snap) return;
    if (!window.confirm(`Restore the snapshot from ${new Date(snap.at).toLocaleString()}?\nThis replaces ALL current parts in this world.`)) return;
    interface P { partId: bigint; worldId: bigint }
    const ids: bigint[] = [];
    for (const p of conn.db.part.iter() as Iterable<P>) if (p.worldId === worldId) ids.push(p.partId);
    const r = conn.reducers as unknown as {
      deletePart(a: { partId: bigint }): Promise<void>;
      rezPart(a: Record<string, unknown>): Promise<void>;
      rezCatalogPart(a: Record<string, unknown>): Promise<void>;
    };
    this.setNote(`Restoring — removing ${ids.length} parts…`, '');
    for (const id of ids) await r.deletePart({ partId: id }).catch(() => undefined);
    let placed = 0, skipped = 0;
    for (const raw of snap.parts as Array<Record<string, unknown>>) {
      const base = {
        worldId,
        posX: raw.posX, posY: raw.posY, posZ: raw.posZ,
        rotX: raw.rotX, rotY: raw.rotY, rotZ: raw.rotZ, rotW: raw.rotW,
        scaleX: raw.scaleX, scaleY: raw.scaleY, scaleZ: raw.scaleZ,
        physics: raw.physics,
      };
      try {
        if (raw.primType === 'mesh') {
          if (!raw.meshRef) { skipped++; continue; }
          await r.rezCatalogPart({ ...base, prefabKey: raw.meshRef });
        } else {
          await r.rezPart({ ...base, primType: raw.primType, shapeJson: raw.shapeJson, materialJson: raw.materialJson });
        }
        placed++;
      } catch { skipped++; }
    }
    this.setNote(`Snapshot restored — ${placed} parts placed${skipped ? `, ${skipped} skipped (uploaded meshes / rejected)` : ''}.`, skipped ? '' : 'ok');
  }

}
